import { Typography } from 'antd';
import type { TabsProps } from 'antd/lib/tabs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import type { Module, Route } from '@tools/ui/types';
import useRouter from '@tools/ui-hooks/useRouter.hook';
import { StyledTabs } from './StyledTabs';

const { Text } = Typography;

const StyledTab = styled(NavLink)`
  & .nav-link {
    padding-bottom: 12px;
    padding-top: 12px;
    transition: opacity 150ms ease-in;
    &:hover {
      opacity: 0.8;
    }
  }
  &.active .nav-link {
    color: ${({ theme }) => theme.colors.link};
  }
`;

type TProps = TabsProps & {
  routes: (Module | Route)[];
  localeI18?: string;
  getTabAmount?: (nav: string) => JSX.Element;
  tabBarExtraContent?: React.ReactNode;
  urlParam?: string;
};

const TabsNavigator: React.FC<React.PropsWithChildren<TProps>> = ({
  getTabAmount,
  routes,
  localeI18,
  tabPosition,
  tabBarExtraContent,
  urlParam,
  ...props
}) => {
  const { t } = useTranslation();
  const { match, pathname } = useRouter();

  const visibleRoutes = React.useMemo(
    () => routes.filter((route) => !route.hideFromMenu),
    [routes]
  );

  const activeKey = React.useMemo(
    () =>
      visibleRoutes.find((path) =>
        pathname.split('/').find((pathname) => pathname === path.name)
      )?.name,
    [pathname, visibleRoutes]
  );

  const param = urlParam ? `/${urlParam}` : '';

  return (
    <StyledTabs
      tabPosition={tabPosition}
      activeKey={activeKey}
      tabBarExtraContent={tabBarExtraContent}
      {...props}
      className="nav_tabs"
      items={visibleRoutes.map(({ name: tabName, displayName }) => {
        let displayText;
        if (displayName) {
          displayText = displayName;
        } else {
          displayText = localeI18
            ? t(`${localeI18}.tabs.${tabName}` as any)
            : decodeURIComponent(tabName);
        }
        return {
          key: tabName,
          label: (
            <StyledTab to={`${match.url}/${tabName}${param}`}>
              <Text className="nav-link">
                <>
                  {displayText} {getTabAmount?.(tabName)}
                </>
              </Text>
            </StyledTab>
          )
        };
      })}
    />
  );
};

export default React.memo(TabsNavigator);
